import { useSearchParams } from 'react-router-dom';
import { Box, Svg, Text } from '@hopskipdrive/react-components';
import { Cancel, Check, DateRangeRounded, ErrorOutline, SwapVert } from '@mui/icons-material';
import { capitalize } from 'lodash';

import { getPriorityLabel } from '../../../utils/IncidentTypeDropdown/IncidentTypeDropdown.helper';
import { priorityLevelMapper, sortMapper, ValueIcon } from '../FilterPanel.helper';

import { FilterChip, PriorityIcon, StyledClearButton } from './FiltersSelected.styles';

export const FiltersSelected = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const startDate = searchParams.get('startDate') || null;
  const endDate = searchParams.get('endDate') || null;
  const status = searchParams.get('status') || null;
  const offenderTypeString = searchParams.get('offenderType') || null;
  const offenderType = offenderTypeString ? JSON.parse(decodeURIComponent(offenderTypeString)) : null;
  const accountsString = searchParams.get('accountIds') || null;
  const selectedAccounts = accountsString ? JSON.parse(decodeURIComponent(accountsString)) : [];
  const ridersString = searchParams.get('riderIds') || null;
  const selectedRiders = ridersString ? JSON.parse(decodeURIComponent(ridersString)) : [];
  const driversString = searchParams.get('driverIds') || null;
  const selectedDrivers = driversString ? JSON.parse(decodeURIComponent(driversString)) : [];
  const priority = searchParams.get('priority') || null;
  const classification = searchParams.get('classificationLabel') || null;
  const classificationPriority = searchParams.get('classificationPriority') || null;
  const subclassification = searchParams.get('subclassificationLabel') || null;
  const sortDir = searchParams.get('sortDir') || null;
  const sortField = searchParams.get('sortField') || null;

  const hasParams =
    startDate ||
    endDate ||
    status ||
    offenderTypeString ||
    accountsString ||
    ridersString ||
    driversString ||
    priority ||
    classification ||
    subclassification ||
    sortDir ||
    sortField;

  const handleRemoveParams = (params, value = null) => {
    const updatedParams = new URLSearchParams(searchParams);

    params.forEach((param) => {
      const paramValue = searchParams.get(param);

      if (paramValue) {
        try {
          const decodedValue = decodeURIComponent(paramValue);
          let parsedValue;

          try {
            parsedValue = JSON.parse(decodedValue);
          } catch (error) {
            parsedValue = decodedValue;
          }

          if (Array.isArray(parsedValue)) {
            let updatedArray = parsedValue;

            updatedArray = updatedArray.filter((item) => item.value !== value);

            if (updatedArray.length === 0) {
              updatedParams.delete(param);
            } else {
              updatedParams.set(param, encodeURIComponent(JSON.stringify(updatedArray)));
            }
          } else {
            updatedParams.delete(param);
          }
        } catch (error) {
          updatedParams.delete(param);
        }
      }
    });

    setSearchParams(updatedParams);
  };

  const clearAllParams = () => {
    setSearchParams({});
  };

  return (
    <Box display="flex" gap="1.19rem" marginTop={hasParams ? '10px' : 0} flexWrap="wrap">
      {startDate && endDate && (
        <FilterChip
          icon={<DateRangeRounded />}
          label={`${startDate} - ${endDate}`}
          deleteIcon={<Cancel />}
          onDelete={() => handleRemoveParams(['startDate', 'endDate'])}
        />
      )}
      {status && (
        <FilterChip
          className={`status-${status}`}
          icon={status === 'resolved' ? <Check /> : <ErrorOutline />}
          label={capitalize(status)}
          deleteIcon={<Cancel />}
          onDelete={() => handleRemoveParams(['status'])}
        />
      )}
      {offenderType && (
        <FilterChip
          icon={<ValueIcon value={offenderType.value} />}
          label={`Alleged Offender: ${offenderType.label}`}
          deleteIcon={<Cancel />}
          onDelete={() => handleRemoveParams(['offenderType'])}
        />
      )}
      {selectedAccounts &&
        selectedAccounts.map((account) => (
          <FilterChip
            key={account.value}
            icon={<ValueIcon value={'accountIds'} />}
            label={account.label}
            deleteIcon={<Cancel />}
            onDelete={() => handleRemoveParams(['accountIds'], account.value)}
          />
        ))}
      {selectedRiders &&
        selectedRiders.map((rider) => (
          <FilterChip
            key={rider.value}
            icon={<ValueIcon value={'riderIds'} />}
            label={rider.label}
            deleteIcon={<Cancel />}
            onDelete={() => handleRemoveParams(['riderIds'], rider.value)}
          />
        ))}
      {selectedDrivers &&
        selectedDrivers.map((driver) => (
          <FilterChip
            key={driver.value}
            icon={<ValueIcon value={'driverIds'} />}
            label={driver.label}
            deleteIcon={<Cancel />}
            onDelete={() => handleRemoveParams(['driverIds'], driver.value)}
          />
        ))}
      {priority && (
        <FilterChip
          icon={
            <PriorityIcon className={priorityLevelMapper[priority]}>
              <Text>P{priority}</Text>
            </PriorityIcon>
          }
          label={capitalize(priorityLevelMapper[priority])}
          deleteIcon={<Cancel />}
          onDelete={() => handleRemoveParams(['priority'])}
        />
      )}
      {classification && classificationPriority && (
        <FilterChip
          icon={
            <PriorityIcon className={getPriorityLabel(parseInt(classificationPriority)).toLowerCase()}>
              <Text>P{classificationPriority}</Text>
            </PriorityIcon>
          }
          label={decodeURIComponent(classification)}
          deleteIcon={<Cancel />}
          onDelete={() => handleRemoveParams(['classification', 'classificationPriority', 'classificationLabel'])}
        />
      )}
      {subclassification && (
        <FilterChip
          label={decodeURIComponent(subclassification)}
          deleteIcon={<Cancel />}
          onDelete={() => handleRemoveParams(['subclassification', 'subclassificationLabel'])}
        />
      )}
      {sortDir && sortField && (
        <FilterChip
          className="sort"
          icon={<SwapVert />}
          label={`Sort By: ${sortMapper(sortDir, sortField)}`}
          deleteIcon={<Cancel />}
          onDelete={() => handleRemoveParams(['sortField', 'sortDir'])}
        />
      )}

      {hasParams && (
        <StyledClearButton variant="text" onClick={clearAllParams}>
          <Svg icon="cross" /> Clear All
        </StyledClearButton>
      )}
    </Box>
  );
};
